<template>
  <div class="project-options">
    <PageHeaderOptions
      :allowChangePIC="allowChangePIC"
      :allowCancel="allowCancel"
      :showSubstitute="isFinalRequestApproved && isPIC && !isProjectCompleted"
      :previousPersonInCharge="currentPersonInCharge"
      :confirmCancelMsg="$t('hint.cancelProject')"
      :itemName="$t('project.name')"
      :site="site"
      @change-pic="val => changeProjectPIC(val)"
      @cancelItem="cancelProject()"
    >
      <li
        v-if="isFinalRequestApproved && isPIC"
        slot="substitute"
      >
        <button
          v-show="!isProjectCompleted"
          class="btn--transparent"
          @click="dispatchCompleteAction()"
        >{{ $t('action.complete') }}</button>
      </li>
    </PageHeaderOptions>
    <HKLModal
      ref="actualCompDateModal"
      :title="$t('project.actualCompDate.forceInputMsg')"
      :closable="true"
      :hasCancelBtn="false"
    />
    <HKLModal
      ref="dlpDateReminder"
      :title="$t('hint.project.withoutDLP')"
      okBtnAction="yesComplete"
      cancelBtnAction="goback"
      @onOkClick="completeProject"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapActions } from 'vuex'

import EventBus from '@/utils/eventBus'
import navigation from '@/utils/navigation'
import userHelper from '@/utils/userHelper'
import { completionType, projectStatus, rateType, rateActionType } from '@/utils/locale'
import { CLOSE_MODAL_SLOT, ActionTypes } from '@/utils/consts'
import { getPurchase } from '@/api/projects/util'
import { aclHelper, aclPermsType } from '@/utils/aclHelper'
import { compareWithThreshold } from '@/utils/projectUtil'

import HKLModal from '@/controls/Modal/HKLModal'
import PageHeaderOptions from '@/modules/PageHeaderOptions'

export default {
  name: 'ProjectOptions',
  components: {
    HKLModal,
    PageHeaderOptions,
  },
  data () {
    return {
      projectCompleteRateType: rateType.PROJECT_COMPLETED,
      projectCompleteDialogId: 'project-complete-dialog',
      projectCompleteActionType: rateActionType.PROJECT_COMPLETED,
      randomRateType: rateType.RANDOM,
      randomActionType: rateActionType.RANDOM_RATE,
      randomDialogId: 'project-random-dialog',
    }
  },
  computed: {
    ...mapGetters('user', ['currentUser', 'isAdmin']),
    ...mapGetters('projects', ['currentProject', 'purchase']),
    ...mapGetters('workPermit', ['currentWorkPermit', 'workPermitsInProject']),
    ...mapGetters('regions', ['enableWorkPermitsFeature']),
    canChangePIC () {
      return this.isAdmin || aclHelper.getAccess(aclPermsType.projects.changePIC)
    },
    allowChangePIC () {
      return this.canChangePIC && this.isActiveItem
    },
    isActiveItem () {
      return !([ projectStatus.CANCELLED, projectStatus.REVISD ].includes(this.currentProject.status))
    },
    allowCancel () {
      return this.isPIC && !([ projectStatus.COMPLETED, projectStatus.CANCELLED, projectStatus.REVISD ].includes(this.currentProject.status))
    },
    isPIC () {
      return this.personInCharge === this.currentUser.username
    },
    personInCharge () {
      const manager = this.currentProject.manager
      return manager && manager.username
    },
    site () {
      const purchase = getPurchase(this.currentProject)
      return purchase && purchase.siteId
    },
    isFinalRequestApproved () {
      const submissions = this.currentProject.submissions || []
      for (let submission of submissions) {
        if ([completionType.FULL, completionType.FINAL_PARTIAL].includes(submission.completionType) &&
          submission.status === 'APPR') {
          return true
        }
      }
      return false
    },
    isProjectCompleted () {
      return this.currentProject.status === projectStatus.COMPLETED
    },
    currentPersonInCharge () {
      const username = deep(this.currentProject, 'manager.username')
      const displayName = deep(this.currentProject, 'manager.displayName')
      return `${userHelper.getUserName(username, displayName)} (${username})`
    },
  },
  methods: {
    ...mapActions('projects', ['updateProject', 'loadProject', 'updateProjectStatus']),
    ...mapActions('workPermit', ['getWorkPermitsInProjectByProjectId']),

    closeModal (dialogId) {
      if (dialogId) {
        EventBus.$emit(`${dialogId}:${CLOSE_MODAL_SLOT}`)
      }
    },

    async changeProjectPIC (newPIC) {
      await this.updateProject({
        projectId: this.currentProject.id,
        request: { managedBy: newPIC.id },
      })
      this.closeModal('change-pic-dialog')
      await this.loadProject(this.currentProject.id)
    },

    async cancelProject () {
      await this.updateProjectStatus({
        id: this.currentProject.id,
        status: projectStatus.CANCELLED,
        action: ActionTypes.projects.cancel,
      })
      this.closeModal('page-header-cancel-dialog')
      navigation.toHome()
    },
    async dispatchCompleteAction () {
      const isPoOverThreshold = await compareWithThreshold(this.purchase)
      const actualCompDate = _.get(this.currentProject, 'actualCompDate')
      if (isPoOverThreshold && _.isEmpty(actualCompDate)) {
        this.$refs.actualCompDateModal.open()
        return
      }
      const dlpDate = _.get(this.currentProject, 'dlpDate')
      if (_.isEmpty(dlpDate)) {
        this.$refs.dlpDateReminder.open()
      } else {
        this.completeProject()
      }
    },
    async completeProject () {
      await this.updateProjectStatus({
        id: this.currentProject.id,
        status: projectStatus.COMPLETED,
        action: ActionTypes.projects.complete,
      })
      await this.loadProject(this.currentProject.id)
      if (this.enableWorkPermitsFeature) {
        await this.getWorkPermitsInProjectByProjectId({projectId: this.currentProject.id, changeCurrentWorkPermitId: this.currentWorkPermit.id})
      }
    },
  },
}
</script>
<style lang="scss" scoped>
@import '~styles/popup';
.project-options {
  font-size: 29px;
  display: inline-block;
  vertical-align: bottom;
}
</style>
<style lang="scss">
#project-random-dialog {
  width: 450px;
}
</style>
